<template>
  <div class="battle-viewer" v-if="frame">
    <div class="bv-bar">
      <button class="bv-btn" @click="step(-1)" :disabled="idx === 0" title="Previous">◀</button>
      <button class="bv-btn" @click="togglePlay" :title="playing ? 'Pause' : 'Play'">{{ playing ? '⏸' : '▶' }}</button>
      <button class="bv-btn" @click="step(1)" :disabled="idx >= frames.length - 1" title="Next">▶</button>
      <input class="bv-scrub" type="range" min="0" :max="frames.length - 1" v-model.number="idx" @input="pause" />
      <span class="bv-count">turn {{ frame.turn }} · {{ idx + 1 }}/{{ frames.length }}</span>
      <select v-model.number="speed" class="bv-speed" title="Speed">
        <option :value="1200">0.5×</option><option :value="700">1×</option><option :value="350">2×</option>
      </select>
    </div>

    <div class="bv-grid" :class="{ 'has-terrain': !!terrain }" :style="gridStyle">
      <div v-for="cell in cells" :key="cell.k" class="bv-cell"
           :class="{ dark: !terrain && cell.dark, 'bv-void': cell.void, 'bv-wall': cell.wall }"
           :style="cell.bg ? { background: cell.bg } : null">
        <span v-if="terrain && cell.h != null" class="bv-h">{{ Math.round(cell.h) }}</span>
        <span v-if="cell.crystal" class="bv-crystal" title="Crystal — a unit that steps here gets full HP/MP">◆</span>
        <div v-if="cell.u" class="bv-token" :class="tokenClass(cell.u)" :style="{ '--tc': colorFor(cell.u.team) }">
          <img class="bv-img" :src="spriteUrl(cell.u)" :alt="cell.u.cls" :title="cell.u.name + ' — ' + cell.u.cls" />
          <span class="bv-face" :class="'f-' + faceDir(cell.u)"></span>
          <span v-if="cell.u.dead != null" class="bv-dead" title="dead — crystallizing">{{ cell.u.dead }}</span>
          <span v-if="hpDelta(cell.u)" class="bv-delta" :class="hpDelta(cell.u) < 0 ? 'dmg' : 'heal'">{{ hpDelta(cell.u) > 0 ? '+' : '' }}{{ hpDelta(cell.u) }}</span>
          <div class="bv-hp"><div class="bv-hp-fill" :class="{ low: hpPct(cell.u) < 34 }" :style="{ width: hpPct(cell.u) + '%' }"></div></div>
          <span v-if="statusTags(cell.u.status).length" class="bv-status"><span v-for="(s, i) in statusTags(cell.u.status)" :key="i" class="bv-st" :class="{ bad: s.bad }">{{ s.label }}</span></span>
        </div>
      </div>
    </div>

    <div class="bv-caption">{{ frame.caption || '…' }}</div>
  </div>
</template>

<script>
import { teamBright } from '../lib/teamColors.js';

import { UNITS_BASE as IMG_URL } from '../lib/assetUrls.js';

export default {
  name: 'BattleViewer',
  props: {
    frames: { type: Array, default: () => [] },
    terrain: { type: Object, default: null }, // real map {w,h,heights,surfaces} — null ⇒ flat grid
    teamA: { type: String, default: '' }, // team labels ("Red"/"Blue"/…/"Champion")
    teamB: { type: String, default: '' },
  },
  data() { return { idx: 0, playing: false, speed: 700, timer: null }; },
  computed: {
    // Grid dimensions: the real map's W×H, else a square that fits every unit.
    cols() { return this.terrain ? this.terrain.w : this.squareSize; },
    rows() { return this.terrain ? this.terrain.h : this.squareSize; },
    squareSize() { let m = 10; for (const f of this.frames) for (const u of f.board) m = Math.max(m, u.x + 1, u.y + 1); return m; },
    gridStyle() { return { gridTemplateColumns: 'repeat(' + this.cols + ', 1fr)', aspectRatio: this.cols + ' / ' + this.rows }; },
    // Min/max walkable height — for normalizing the elevation shading.
    heightRange() {
      if (!this.terrain) return null;
      let lo = Infinity, hi = -Infinity;
      for (const row of this.terrain.heights) for (const h of row) if (h != null) { if (h < lo) lo = h; if (h > hi) hi = h; }
      return hi >= lo ? { lo, hi } : { lo: 0, hi: 0 };
    },
    frame() { return this.frames[this.idx] || null; },
    prevBoard() { return this.idx > 0 ? this.frames[this.idx - 1].board : null; },
    cells() {
      const cols = this.cols, rows = this.rows, t = this.terrain, hr = this.heightRange;
      const at = {};
      for (const u of (this.frame ? this.frame.board : [])) {
        const k = u.y * cols + u.x;
        if (!at[k] || (!at[k].alive && u.alive)) at[k] = u; // a living unit is never hidden by a corpse
      }
      const crys = new Set((this.frame && this.frame.crystals) || []);
      const out = [];
      for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
        const k = y * cols + x;
        let voidT = false, wall = false, h = null, bg = null;
        if (t) {
          h = (t.heights[y] && t.heights[y][x] != null) ? t.heights[y][x] : null;
          wall = !!(t.surfaces && t.surfaces[y] && t.surfaces[y][x] === 'x');
          voidT = h == null;
          if (!voidT) { const f = hr.hi > hr.lo ? (h - hr.lo) / (hr.hi - hr.lo) : 0; bg = `hsl(205, 28%, ${Math.round(13 + 30 * f)}%)`; } // higher = lighter
        }
        out.push({ k, x, y, dark: (x + y) % 2 === 1, u: at[k] || null, crystal: crys.has(x + ',' + y), void: voidT, wall, h, bg });
      }
      return out;
    },
    actorColor() { const u = (this.frame.board || []).find((b) => b.name === this.frame.actor); return u ? this.colorFor(u.team) : '#fff'; },
  },
  watch: {
    frames() { this.pause(); this.idx = 0; },
  },
  beforeUnmount() { this.pause(); },
  methods: {
    colorFor(team) { return teamBright(team === 'A' ? this.teamA : this.teamB) || '#888'; },
    spriteUrl(u) {
      const label = u.team === 'A' ? this.teamA : this.teamB;
      const g = (u.gender && u.gender[0]) || 'M';
      if (u.gender === 'Monster') return IMG_URL + u.cls + '.gif';
      if (label === 'Champion') return IMG_URL + u.cls + g + '.gif';
      return IMG_URL + u.cls + g + '_' + label + '.gif';
    },
    hpPct(u) { return u.maxHp ? Math.max(0, Math.min(100, Math.round((u.hp / u.maxHp) * 100))) : 0; },
    hpDelta(u) {
      if (!this.prevBoard) return 0;
      const p = this.prevBoard.find((b) => b.name === u.name && b.team === u.team);
      return p ? u.hp - p.hp : 0;
    },
    faceDir(u) { const d = u.dir || {}; if (d.y > 0) return 's'; if (d.y < 0) return 'n'; if (d.x > 0) return 'e'; return 'w'; },
    statusTags(s) {
      const TAG = { 'Death Sentence': '☠', Haste: '⏩', Slow: '⏪', Protect: 'Pr', Shell: 'Sh', Regen: '+', Poison: 'Po', Sleep: 'Zz', Stop: 'St', Charm: '♥', Petrify: 'Pf', "Don't Act": 'DA', Reraise: 'RR', Berserk: 'Br' };
      const BAD = new Set(['Death Sentence', 'Slow', 'Poison', 'Sleep', 'Stop', 'Charm', 'Petrify', "Don't Act", 'Frog']);
      return Object.entries(s || {})
        .sort((a, b) => (b[0] === 'Death Sentence' ? 1 : 0) - (a[0] === 'Death Sentence' ? 1 : 0))
        .map(([k, v]) => ({ label: (TAG[k] || k.slice(0, 2)) + (k === 'Death Sentence' ? v : ''), bad: BAD.has(k) }));
    },
    tokenClass(u) {
      const d = this.hpDelta(u);
      return { actor: this.frame.actor && u.name === this.frame.actor, dead: !u.alive, hit: d < 0, healed: d > 0 };
    },
    step(n) { this.pause(); this.idx = Math.min(this.frames.length - 1, Math.max(0, this.idx + n)); },
    togglePlay() { this.playing ? this.pause() : this.play(); },
    play() { if (this.idx >= this.frames.length - 1) this.idx = 0; this.playing = true; this.tick(); },
    tick() {
      clearTimeout(this.timer);
      if (!this.playing) return;
      this.timer = setTimeout(() => {
        if (this.idx < this.frames.length - 1) { this.idx++; this.tick(); } else { this.playing = false; }
      }, this.speed);
    },
    pause() { this.playing = false; clearTimeout(this.timer); },
  },
};
</script>

<style scoped>
.battle-viewer { color: #fff; }
.bv-bar { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; flex-wrap: wrap; }
.bv-btn, .bv-speed { background: rgba(12, 54, 80, 0.7); color: #fff; border: 1px solid #2f5d86; border-radius: 4px; font-size: 13px; padding: 2px 9px; cursor: pointer; }
.bv-btn:disabled { opacity: 0.4; cursor: default; }
.bv-scrub { flex: 1; min-width: 90px; accent-color: #4a9fd8; }
.bv-count { font-size: 11px; opacity: 0.8; font-variant-numeric: tabular-nums; white-space: nowrap; }
.bv-grid { display: grid; gap: 1px; width: 100%; max-width: 380px; margin: 0 auto; aspect-ratio: 1 / 1; background: rgba(255, 255, 255, 0.08); border: 1px solid #2f5d86; border-radius: 4px; padding: 2px; }
.bv-grid.has-terrain { max-width: 460px; background: rgba(0, 0, 0, 0.25); }
.bv-cell { position: relative; background: rgba(0, 0, 0, 0.18); }
.bv-cell.dark { background: rgba(0, 0, 0, 0.32); }
.bv-cell.bv-void { background: transparent !important; } /* off-map / chasm — not a tile */
.bv-cell.bv-wall { box-shadow: inset 0 0 0 1px rgba(180, 70, 70, 0.5); } /* blocked surface */
.bv-h { position: absolute; top: 0; left: 1px; font-size: 7px; line-height: 1; opacity: 0.4; color: #cfe6ff; pointer-events: none; z-index: 0; font-variant-numeric: tabular-nums; }
.bv-token { position: absolute; inset: 1px; border: 2px solid var(--tc); border-radius: 4px; overflow: visible; background: rgba(0, 0, 0, 0.35); transition: inset 0.18s; }
.bv-img { width: 100%; height: 100%; object-fit: contain; image-rendering: pixelated; display: block; }
.bv-token.actor { box-shadow: 0 0 0 2px #fff, 0 0 8px 2px var(--tc); z-index: 3; }
.bv-token.dead { filter: grayscale(1) brightness(0.5); opacity: 0.45; }
.bv-token.hit { animation: bv-hit 0.5s ease-out; }
.bv-token.healed { animation: bv-heal 0.5s ease-out; }
@keyframes bv-hit { 0% { box-shadow: 0 0 0 3px #e44; } 100% { box-shadow: none; } }
@keyframes bv-heal { 0% { box-shadow: 0 0 0 3px #5d5; } 100% { box-shadow: none; } }
.bv-hp { position: absolute; left: 1px; right: 1px; bottom: 1px; height: 3px; background: rgba(0, 0, 0, 0.6); border-radius: 2px; overflow: hidden; }
.bv-hp-fill { height: 100%; background: #5fd35f; }
.bv-hp-fill.low { background: #e0683a; }
.bv-delta { position: absolute; top: -8px; right: -4px; font-size: 10px; font-weight: bold; text-shadow: 0 0 3px #000; z-index: 4; }
.bv-delta.dmg { color: #ff7a7a; }
.bv-delta.heal { color: #8cf08c; }
.bv-status { position: absolute; top: -3px; left: -2px; display: flex; flex-wrap: wrap; gap: 1px; max-width: 130%; z-index: 4; pointer-events: none; }
.bv-st { font-size: 7px; line-height: 1; padding: 1px 2px; border-radius: 2px; background: rgba(40, 90, 140, 0.92); color: #fff; }
.bv-st.bad { background: rgba(170, 45, 45, 0.95); }
.bv-crystal { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #9fe8ff; text-shadow: 0 0 6px #4ad, 0 0 2px #fff; z-index: 1; animation: bv-crys 1.5s ease-in-out infinite; }
@keyframes bv-crys { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
.bv-dead { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 12px; font-weight: bold; color: #fff; text-shadow: 0 0 4px #000, 0 0 3px #000; z-index: 5; }
.bv-face { position: absolute; width: 0; height: 0; z-index: 2; }
.bv-face.f-n { top: -1px; left: 50%; transform: translateX(-50%); border-left: 3px solid transparent; border-right: 3px solid transparent; border-bottom: 4px solid #fff; }
.bv-face.f-s { bottom: -1px; left: 50%; transform: translateX(-50%); border-left: 3px solid transparent; border-right: 3px solid transparent; border-top: 4px solid #fff; }
.bv-face.f-e { right: -1px; top: 50%; transform: translateY(-50%); border-top: 3px solid transparent; border-bottom: 3px solid transparent; border-left: 4px solid #fff; }
.bv-face.f-w { left: -1px; top: 50%; transform: translateY(-50%); border-top: 3px solid transparent; border-bottom: 3px solid transparent; border-right: 4px solid #fff; }
.bv-caption { margin-top: 8px; font-size: 12px; min-height: 1.4em; text-align: center; opacity: 0.95; }
</style>
